// src/components/clinica/ResumenFicha.js
// =========================================================================
// 📄 Lectura de una consulta anterior con la plantilla de su especialidad
// (para el historial). Solo muestra los campos que tienen algún dato.
// =========================================================================
import React from 'react';
import { useClinica } from '../../context/ClinicaContext';

const CONSTANTES = { tension: ['Tensión', 'mmHg'], ritmoCardiaco: ['Ritmo', 'lpm'], temperatura: ['Temp.', '°C'], saturacionO2: ['Sat. O2', '%'] };

function texto(campo, valor, estadosDiente) {
    switch (campo.tipo) {
        case 'sino': return valor ? 'Sí' : 'No';
        case 'casillas': return valor.join(', ');
        case 'escala': return `${valor} / ${campo.max}`;
        case 'numero': return `${valor}${campo.unidad ? ` ${campo.unidad}` : ''}`;
        case 'constantes':
            return Object.entries(valor).filter(([, v]) => v)
                .map(([k, v]) => `${CONSTANTES[k]?.[0] || k}: ${v} ${CONSTANTES[k]?.[1] || ''}`.trim()).join(' · ');
        case 'odontograma': {
            const etiquetas = Object.fromEntries((estadosDiente || []).map(e => [e.valor, e.etiqueta]));
            return Object.entries(valor).sort(([a], [b]) => a.localeCompare(b))
                .map(([pieza, estado]) => `${pieza}: ${etiquetas[estado] || estado}`).join(' · ');
        }
        default: return String(valor);
    }
}

const tieneDato = (v) => !(v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0)
    || (typeof v === 'object' && !Array.isArray(v) && !Object.values(v).some(Boolean)));

export default function ResumenFicha({ consulta }) {
    const { definicion, odontogramaCatalogo } = useClinica();
    const plantilla = definicion(consulta?.especialidad);
    if (!consulta) return null;

    if (!plantilla) {
        return (
            <div className="space-y-3">
                <p className="text-sm font-bold text-slate-800">{consulta.diagnostico}</p>
                <p className="text-sm font-semibold text-slate-800">{consulta.tratamiento}</p>
            </div>
        );
    }

    const valorDe = (campo) => (campo.base ? consulta[campo.id] : consulta.datosEspecialidad?.[campo.id]);
    const secciones = plantilla.historia.secciones
        .map(s => ({ ...s, campos: s.campos.filter(c => tieneDato(valorDe(c))) }))
        .filter(s => s.campos.length);

    return (
        <div className="space-y-6">
            <span className="inline-block px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest text-white" style={{ backgroundColor: plantilla.color }}>
                {plantilla.nombre}
            </span>
            {secciones.length === 0 && <p className="text-sm font-semibold text-slate-400">Sin datos clínicos registrados.</p>}
            {secciones.map(seccion => (
                <section key={seccion.id}>
                    <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">{seccion.titulo}</h4>
                    <dl className="space-y-3">
                        {seccion.campos.map(campo => (
                            <div key={campo.id} className="bg-white border border-slate-200 rounded-xl p-3">
                                <dt className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">{campo.etiqueta}</dt>
                                <dd className="text-sm font-semibold text-slate-800 mt-1 whitespace-pre-line">{texto(campo, valorDe(campo), odontogramaCatalogo?.estados)}</dd>
                            </div>
                        ))}
                    </dl>
                </section>
            ))}
        </div>
    );
}
